// Expected minus recorded, per member.
//
// A pure function over the manifest, the member's marker and the member's step
// rows. No database handle, no clock, no I/O — the operator CLI in
// `scripts/db-tooling` owns all of that, so every branch here is reachable from
// a unit test and the report's meaning does not depend on how it was fetched.
//
// The report's job is to be actionable, which means the three settled-step cases
// must not be lumped together. A step that correctly found nothing to do is not a
// finding; a step a re-run could clear belongs in a replay batch; a step waiting
// on a person belongs on somebody's list. The reason code decides which, through
// `classifyStepReason`.

import {
  MIGRATION_TIER_DISPATCH,
  classifyStepReason,
  manifestStepsForTier,
  type MigrationStepReasonClass,
} from './step-manifest';
import type { MemberMigrationStepCounts, MemberMigrationTier } from './schema';

/** The marker fields the report reads. */
export interface GapReportMarker {
  readonly status: string;
}

/** The ledger fields the report reads, whatever query produced them. */
export interface GapReportStepRow {
  readonly stepName: string;
  readonly tier: string;
  readonly status: string;
  readonly attempts: number;
  readonly error: string | null;
  readonly counts: MemberMigrationStepCounts | null;
  readonly warnings: readonly string[] | null;
}

/** One reported step, carrying what the ledger knows about it. */
export interface GapReportStep {
  readonly step: string;
  /** `missing` when the manifest expects the step and no row exists. */
  readonly status: string;
  readonly attempts: number;
  readonly error: string | null;
  readonly counts: MemberMigrationStepCounts | null;
  readonly warnings: readonly string[] | null;
  /**
   * What the recorded reason says the step is waiting on, for a row that
   * settled — `failed` or `skipped`. `null` for a `missing` or `incomplete`
   * step, which has recorded no outcome to classify.
   *
   * Carried on `failed` rows as well as skipped ones because a failure is not
   * always something a re-run clears: a 1.0 row that cannot be parsed or a
   * Healthie id another member holds fails every time, and the replay resolver
   * keeps such a step out of every replay until a person deals with it. The
   * class is what lets an operator see that before replaying anything.
   */
  readonly reasonClass: MigrationStepReasonClass | null;
}

export interface GapReportTier {
  readonly tier: MemberMigrationTier;
  /** False when no runner dispatches this tier yet; `missing` is then empty. */
  readonly dispatched: boolean;
  /** Expected by the manifest, absent from the ledger. */
  readonly missing: readonly GapReportStep[];
  readonly failed: readonly GapReportStep[];
  /**
   * A row that never reached a terminal status — still `pending` or `running`.
   *
   * Its own class, because it is neither missing nor settled and would otherwise
   * fall through every bucket and read as complete. A step whose `completeStep`
   * threw after its work landed is left `running` on purpose, so that a retry's
   * `beginStep` picks it up; when the retries run out into the DLQ it stays
   * `running` for good, and that is exactly the incomplete migration this report
   * exists to surface.
   */
  readonly incomplete: readonly GapReportStep[];
  /** Skipped for a reason a re-run could clear. */
  readonly retryable: readonly GapReportStep[];
  /** Skipped for a reason needing a person — `human_reconciliation` and kin. */
  readonly human: readonly GapReportStep[];
  /** Skipped because there was nothing to do. Not a gap; reported for the tally. */
  readonly settled: readonly GapReportStep[];
}

export interface MemberGapReport {
  readonly memberId: string;
  readonly markerStatus: string | null;
  readonly tiers: readonly GapReportTier[];
  /**
   * The marker says Tier 0 completed but the member has no Tier 1 rows at all —
   * so the `member.migrated` publish that fans Tier 1 out never reached a
   * consumer. Distinct from "some Tier 1 steps are missing", which is an
   * incomplete run rather than a lost event.
   */
  readonly lostMigratedPublish: boolean;
  /** True when any tier reports a missing, failed, retryable or human step. */
  readonly hasGap: boolean;
}

export interface ComputeMemberGapReportInput {
  readonly memberId: string;
  readonly marker: GapReportMarker | undefined;
  readonly steps: readonly GapReportStepRow[];
  /** Which tiers to report on; defaults to all three. */
  readonly tiers?: readonly MemberMigrationTier[];
}

const ALL_TIERS: readonly MemberMigrationTier[] = ['tier0', 'tier1', 'tier2'];

const MISSING_STATUS = 'missing';

function reportedStep(row: GapReportStepRow): GapReportStep {
  const settled = row.status === 'failed' || row.status === 'skipped';
  return {
    step: row.stepName,
    status: row.status,
    attempts: row.attempts,
    error: row.error,
    counts: row.counts,
    warnings: row.warnings,
    reasonClass: settled ? classifyStepReason(row.error) : null,
  };
}

function missingStep(step: string): GapReportStep {
  return {
    step,
    status: MISSING_STATUS,
    attempts: 0,
    error: null,
    counts: null,
    warnings: null,
    reasonClass: null,
  };
}

export function computeMemberGapReport(input: ComputeMemberGapReportInput): MemberGapReport {
  const tiers = input.tiers ?? ALL_TIERS;
  const byStep = new Map(input.steps.map((r) => [r.stepName, r] as const));

  const reported: GapReportTier[] = tiers.map((tier) => {
    const dispatched = MIGRATION_TIER_DISPATCH[tier];
    const expected = manifestStepsForTier(tier);

    // An undispatched tier has no expected-but-absent population: naming every
    // step for every migrated member would bury the tiers that do run.
    const missing = dispatched ? expected.filter((step) => !byStep.has(step)).map(missingStep) : [];

    const failed: GapReportStep[] = [];
    const incomplete: GapReportStep[] = [];
    const byClass: Record<MigrationStepReasonClass, GapReportStep[]> = {
      settled: [],
      retryable: [],
      human: [],
    };

    // Every row of this tier, not only the expected ones — a step the manifest
    // has since dropped still failed, and hiding it would lose the finding.
    //
    // Switch on the whole status vocabulary rather than picking off the two
    // interesting values: a row in `pending` or `running` has a row, so it is not
    // missing, and it has not settled, so it is in no reason class. Handling only
    // `failed` and `skipped` left it in no bucket at all and the member read as
    // complete.
    for (const row of input.steps) {
      if (row.tier !== tier) continue;
      if (row.status === 'failed') {
        failed.push(reportedStep(row));
      } else if (row.status === 'skipped') {
        byClass[classifyStepReason(row.error)].push(reportedStep(row));
      } else if (row.status !== 'done') {
        incomplete.push(reportedStep(row));
      }
    }

    return {
      tier,
      dispatched,
      missing,
      failed,
      incomplete,
      retryable: byClass.retryable,
      human: byClass.human,
      settled: byClass.settled,
    };
  });

  const tier1RowCount = input.steps.filter((r) => r.tier === 'tier1').length;
  const lostMigratedPublish = input.marker?.status === 'done' && tier1RowCount === 0;

  const hasGap =
    lostMigratedPublish ||
    reported.some(
      (t) =>
        t.missing.length > 0 ||
        t.failed.length > 0 ||
        t.incomplete.length > 0 ||
        t.retryable.length > 0 ||
        t.human.length > 0,
    );

  return {
    memberId: input.memberId,
    markerStatus: input.marker?.status ?? null,
    tiers: reported,
    lostMigratedPublish,
    hasGap,
  };
}
